<template>
    <modal @close="$emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true" size="lg">
        <template v-slot:title>Add Employee</template>
        <template v-slot:subheading v-if="stepSubheading"><h5>{{ stepSubheading }}</h5></template>

        <slot>
            <employer-hire-hr-info
                v-if="step == 'hr-info'"
                :client="client"
                :employer-hire="employerHire"
                ref="hrInfoComponent"
                @updated="onHRInfoUpdated($event)"
                @pristine_status="isCloseable = $event"
                @form_errors="formErrors = $event"
                :hide-form-errors="true"
            />
            <employer-hire-jobs
                v-if="step == 'jobs'"
                :client="client"
                :employer-hire="employerHire"
                ref="jobsComponent"
                @updated="onJobsUpdated($event)"
                @pristine_status="isCloseable = $event"
                @form_errors="formErrors = $event"
                :hide-form-errors="true"
            />
            <employer-hire-address
                v-if="step == 'address'"
                :client="client"
                :employer-hire="employerHire"
                ref="addressComponent"
                @updated="onAddressUpdated($event)"
                @pristine_status="isCloseable = $event"
                @form_errors="formErrors = $event"
                :hide-form-errors="true"
            />
            <employer-hire-address-correction
                v-if="step == 'address-correction'"
                :client="client"
                :employer-hire="employerHire"
                ref="addressCorrectionComponent"
                @updated="onAddressCorrectionUpdated($event)"
                @form_errors="formErrors = $event"
                :hide-form-errors="true"
            />
            <employer-hire-tax-params
                v-if="step == 'tax-params'"
                :client="client"
                :employer-hire="employerHire"
                ref="taxParamsComponent"
                @updated="onTaxParamsUpdated($event)"
                @form_errors="formErrors = $event"
                :hide-form-errors="true"
            />
        </slot>
        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <div class="form-row">
                <div class="col">
                    <button v-if="step == 'hr-info'" type="button" class="btn btn-block btn-outline-primary" @click.prevent="close(true)">Cancel</button>
                    <button v-else type="button" class="btn btn-block btn-outline-primary" @click.prevent="prevStep">Back</button>
                </div>
                <div class="col">
                    <button v-if="step == 'tax-params'" type="submit" class="btn btn-block btn-primary" @click.prevent="nextStep">Add Employee</button>
                    <button v-else type="submit" class="btn btn-block btn-primary" @click.prevent="nextStep">Continue</button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import ModalMixin from '@/mixins/ModalMixin'
import api from '@/api'
import EmployerHireHrInfo from './EmployerHireHrInfo'
import EmployerHireJobs from './EmployerHireJobs'
import EmployerHireAddress from './EmployerHireAddress'
import EmployerHireAddressCorrection from './EmployerHireAddressCorrection'
import EmployerHireTaxParams from './EmployerHireTaxParams'

export default {
    mixins: [ModalMixin],
    components: {
        EmployerHireHrInfo,
        EmployerHireJobs,
        EmployerHireAddress,
        EmployerHireAddressCorrection,
        EmployerHireTaxParams,
    },
    props: ['client'],
    data() {
        return {
            step: 'hr-info',
            employerHire: null,
            formErrors: [],
        }
    },
    computed: {
        stepSubheading() {
            const headings = {
                'hr-info': 'HR Information',
                'jobs': 'Add Job',
                'address': 'Resident Address',
                'address-correction': 'Resident Address',
                'tax-params': 'Tax Withholding',
            }
            return headings[this.step] || null
        },
    },
    methods: {
        nextStep() {
            if (this.step == 'hr-info') {
                this.$refs.hrInfoComponent.onSubmit()
            } else if (this.step == 'jobs') {
                this.$refs.jobsComponent.onSubmit()
            } else if (this.step == 'address') {
                this.$refs.addressComponent.onSubmit()
            } else if (this.step == 'address-correction') {
                this.$refs.addressCorrectionComponent.onSubmit()
            } else if (this.step == 'tax-params') {
                this.$refs.taxParamsComponent.onSubmit()
            }
        },
        prevStep() {
            if (this.step == 'tax-params') {
                this.step = this.employerHire?.has_address_correction_step ? 'address-correction' : 'address'
            } else if (this.step == 'address-correction') {
                this.step = 'address'
            } else if (this.step == 'address') {
                this.step = 'jobs'
            } else if (this.step == 'jobs') {
                this.step = 'hr-info'
            }
        },
        onHRInfoUpdated(data) {
            this.$store.dispatch('START_LOADING')
            const request = this.employerHire
                ? api.patch(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}?action=hr-info`, data)
                : api.post(`/clients/${this.client.id}/employer-hires`, data)
            request.then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.employerHire = resp
                this.step = 'jobs'
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                const fieldErrors = [...(errors.ssn || []), ...(errors.email || [])]
                const allErrors = [...(errors.__all__ || []), ...fieldErrors]
                this.formErrors = allErrors.length ? allErrors : ['An error occurred. Please try again.']
            })
        },
        onJobsUpdated(data) {
            this.$store.dispatch('START_LOADING')
            api.patch(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}?action=jobs`, data).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.employerHire = resp
                this.step = 'address'
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__ || ['An error occurred. Please try again.']
            })
        },
        onAddressUpdated(data) {
            this.$store.dispatch('START_LOADING')
            api.patch(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}?action=address`, data).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.employerHire = resp
                if (resp.has_address_correction_step) {
                    this.step = 'address-correction'
                } else {
                    this.step = 'tax-params'
                }
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__ || ['An error occurred. Please try again.']
            })
        },
        onAddressCorrectionUpdated(data) {
            this.$store.dispatch('START_LOADING')
            api.patch(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}?action=address-correction`, data).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.employerHire = resp
                this.step = 'tax-params'
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__ || ['An error occurred. Please try again.']
            })
        },
        onTaxParamsUpdated(data) {
            this.$store.dispatch('START_LOADING')
            api.patch(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}/sym-tax-config`, data).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.onSubmit()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__ || ['An error occurred. Please try again.']
            })
        },
        onSubmit(evt) {
            this.$store.dispatch('START_LOADING')
            api.post(`/clients/${this.client.id}/employer-hires/${this.employerHire.id}/confirm`, {}).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('employee_updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = errors.__all__ || ['An error occurred. Please try again.']
            })
        },
    }
}
</script>
